/**
 * Platform Header Component
 *
 * Main header component that combines all platform shell components:
 * - App Switcher (waffle menu)
 * - Context Switcher (admin/app/portal)
 * - Notification Bell
 * - User Menu
 *
 * This is the primary component to embed in applications.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { User, BreadcrumbItem, NavigationContextUpdate, MenuItemSnapshot, MenuRegion } from '../types';
import { configure, configureNotifications, configurePresence, setToken, authAPI } from '../services/api';
import { wsService } from '../services/websocket';
import { eventBus, Events } from '../services/events';

// Import child components
import './app-switcher';
import './notification-bell';
import './user-menu';
import './context-switcher';
import './organization-switcher';
import './global-search';
import './messenger-icon';
import './app-context';
import { brandingService } from '../services/branding';

@customElement('moya-platform-header')
export class MoyaPlatformHeader extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    /* Skip-to-content link (WCAG 2.4.1) - visually hidden until focused */
    .skip-link {
      position: absolute;
      top: -100px;
      left: 8px;
      z-index: 10000;
      padding: 8px 16px;
      background: #1d4ed8;
      color: white;
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      border-radius: 0 0 6px 6px;
      transition: top 0.15s ease;
    }

    .skip-link:focus {
      top: 0;
      outline: 2px solid #fbbf24;
      outline-offset: 2px;
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 56px;
      padding: 0 16px;
      background: linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%);
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      color: white;
    }

    /* Light theme override - applied when host has data-theme="light" */
    :host([data-theme='light']) .header {
      background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(241, 245, 249, 0.98) 100%);
      border-bottom-color: rgba(15, 23, 42, 0.12);
      color: #0f172a;
    }

    :host([data-theme='light']) .logo-text,
    :host([data-theme='light']) .app-name {
      color: #0f172a;
    }

    :host([data-theme='light']) .app-name {
      color: rgba(15, 23, 42, 0.6);
      border-left-color: rgba(15, 23, 42, 0.15);
    }

    :host([data-theme='light']) .divider {
      background: rgba(15, 23, 42, 0.1);
    }

    :host([data-theme='light']) .sign-in-btn {
      border-color: rgba(15, 23, 42, 0.3);
      color: #0f172a;
    }

    :host([data-theme='light']) .sign-in-btn:hover {
      background: rgba(15, 23, 42, 0.05);
      border-color: rgba(15, 23, 42, 0.5);
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .header-center {
      display: flex;
      align-items: center;
      gap: 16px;
    }

    .header-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .logo {
      display: flex;
      align-items: center;
      gap: 10px;
      text-decoration: none;
      color: inherit;
      border-radius: 6px;
    }

    /* Visible focus indicator (WCAG 2.4.7) */
    .logo:focus-visible,
    .sign-in-btn:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .logo-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .logo-icon svg {
      width: 18px;
      height: 18px;
      color: white;
    }

    .logo-text {
      font-size: 16px;
      font-weight: 600;
      color: white;
    }

    .app-name {
      font-size: 14px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.7);
      padding-left: 12px;
      border-left: 1px solid rgba(255, 255, 255, 0.2);
      margin-left: 4px;
    }

    .divider {
      width: 1px;
      height: 24px;
      background: rgba(255, 255, 255, 0.1);
      margin: 0 4px;
    }

    .sign-in-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 16px;
      border: 1px solid rgba(255, 255, 255, 0.3);
      border-radius: 6px;
      background: transparent;
      color: white;
      font-size: 13px;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      transition: all 0.15s ease;
    }

    .sign-in-btn:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.5);
    }

    .sign-in-btn svg {
      width: 16px;
      height: 16px;
    }

    @media (max-width: 640px) {
      .logo-text,
      .app-name {
        display: none;
      }

      .header {
        height: 48px;
        padding: 0 8px;
      }

      .header-left,
      .header-right {
        gap: 4px;
      }

      .header-center {
        gap: 8px;
      }

      .divider {
        margin: 0 2px;
      }
    }

    /* Customizable via CSS variables */
    :host {
      --header-bg: linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.98) 100%);
      --header-border: rgba(255, 255, 255, 0.1);
      --header-height: 56px;
    }

    .header {
      background: var(--header-bg);
      border-bottom-color: var(--header-border);
      height: var(--header-height);
    }

    /* ---- Menu-contribution: registered app menu items --------------------- */

    /* nav region: top-bar nav entries near the app name / breadcrumb strip */
    .menu-nav {
      display: flex;
      align-items: center;
      gap: 2px;
    }

    .menu-nav-item {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 6px 10px;
      border: none;
      background: transparent;
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.75);
      font-family: inherit;
      font-size: 13px;
      font-weight: 500;
      text-decoration: none;
      cursor: pointer;
      transition: background-color 0.15s ease, color 0.15s ease;
    }

    .menu-nav-item:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .menu-nav-item:focus-visible,
    .menu-action-item:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .menu-nav-item[disabled],
    .menu-nav-item[aria-disabled='true'],
    .menu-action-item[disabled],
    .menu-action-item[aria-disabled='true'] {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }

    :host([data-theme='light']) .menu-nav-item {
      color: rgba(15, 23, 42, 0.7);
    }

    :host([data-theme='light']) .menu-nav-item:hover {
      background: rgba(15, 23, 42, 0.06);
      color: #0f172a;
    }

    .menu-nav-icon {
      display: inline-flex;
      width: 16px;
      height: 16px;
    }

    .menu-nav-icon svg {
      width: 100%;
      height: 100%;
    }

    /* actions region: icon buttons in the actions cluster next to the bell */
    .menu-action-item {
      position: relative;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border: none;
      background: transparent;
      border-radius: 8px;
      color: rgba(255, 255, 255, 0.75);
      cursor: pointer;
      text-decoration: none;
      transition: background-color 0.15s ease, color 0.15s ease;
    }

    .menu-action-item:hover {
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    :host([data-theme='light']) .menu-action-item {
      color: rgba(15, 23, 42, 0.7);
    }

    :host([data-theme='light']) .menu-action-item:hover {
      background: rgba(15, 23, 42, 0.06);
      color: #0f172a;
    }

    .menu-action-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
      font-size: 13px;
      font-weight: 600;
    }

    .menu-action-icon svg {
      width: 100%;
      height: 100%;
    }

    /* badge shared by nav + actions regions */
    .menu-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 16px;
      height: 16px;
      padding: 0 4px;
      border-radius: 8px;
      background: #ef4444;
      color: white;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
    }

    .menu-action-item .menu-badge {
      position: absolute;
      top: 2px;
      right: 2px;
    }
  `;

  // Configuration properties
  @property({ type: String, attribute: 'api-url' }) apiUrl = '';
  @property({ type: String, attribute: 'presence-api-url' }) presenceApiUrl = '';
  @property({ type: String, attribute: 'ws-url' }) wsUrl = '';
  /**
   * Notification Service base URL, e.g. https://notify.moyasolutions.com.
   *
   * Notifications are a separate service; `api-url` does NOT cover them. When
   * this is unset the bell falls back to `ws-url` for its socket and to
   * `api-url` for its REST calls, which is how every notification request
   * ended up on whichever API the host app happened to configure.
   */
  @property({ type: String, attribute: 'notifications-url' }) notificationsUrl = '';

  /**
   * Absolute URL for the bell's "View all notifications" link.
   *
   * Must be ABSOLUTE. The bell's link was a host-relative `/notifications`,
   * which resolved against whichever app embeds the shell -- in Messenger
   * that is a route which does not exist, so it rendered a blank page.
   * Unset means the bell omits the link rather than offering a dead one.
   */
  @property({ type: String, attribute: 'notifications-app-url' })
  notificationsAppUrl = '';
  @property({ type: String }) token = '';
  @property({ type: String }) appId = '';
  @property({ type: String }) appName = '';
  @property({ type: String }) portalUrl = 'https://login.moyasolutions.com';

  // Feature flags
  @property({ type: Boolean }) showAppSwitcher = true;
  @property({ type: Boolean }) showContextSwitcher = false;
  @property({ type: Boolean }) showOrganizationSwitcher = false;
  @property({ type: Boolean }) showNotifications = true;
  @property({ type: Boolean }) showUserMenu = true;
  @property({ type: Boolean }) showLogo = true;
  @property({
    type: Boolean,
    converter: {
      fromAttribute: (value: string | null) => value !== null,
      toAttribute: (value: boolean) => value ? '' : null
    }
  }) showSearch = false;

  @property({
    type: Boolean,
    converter: {
      fromAttribute: (value: string | null) => value !== null,
      toAttribute: (value: boolean) => value ? '' : null
    }
  }) showMessenger = false;

  // Search configuration
  @property({ type: String }) searchPlaceholder = 'Search...';
  @property({ type: String }) searchUrl = '';
  @property({ type: String, attribute: 'search-api-url' }) searchApiUrl = '';
  @property({ type: Number }) searchDebounceMs = 300;

  // Messenger configuration
  @property({ type: String }) messengerUrl = '';
  @property({ type: String }) messengerApiUrl = '';

  // User menu configuration
  @property({ type: Boolean }) avatarOnly = false;
  @property({ type: String }) userMenuSize: 'sm' | 'md' | 'lg' = 'md';
  @property({ type: Array }) customMenuItems = [];

  // Branding configuration
  @property({ type: String }) logoUrl = '';
  @property({ type: String }) logoText = 'Moya';
  @property({ type: String }) primaryColor = '';

  // Context configuration
  @property({ type: String }) currentContext: 'app' | 'admin' | 'portal' = 'app';
  @property({ type: Array }) availableContexts = [];

  // App context / navigation configuration
  @property({ type: String }) pageTitle = '';
  @property({ type: Array }) breadcrumbs: BreadcrumbItem[] = [];
  @property({ type: Boolean }) showBreadcrumbs = false;
  @property({ type: Boolean }) showPageTitle = true;
  @property({ type: Boolean }) showAppContext = true;

  // Accessibility / theming
  // theme: 'auto' reads prefers-color-scheme; 'light' or 'dark' force a value.
  @property({ type: String, reflect: true, attribute: 'data-theme' }) dataTheme: 'light' | 'dark' = 'dark';
  @property({ type: String }) theme: 'auto' | 'light' | 'dark' = 'auto';
  @property({ type: String, attribute: 'skip-target-id' }) skipTargetId = 'main';
  @property({ type: Boolean, attribute: 'show-skip-link' }) showSkipLink = true;

  // User can be passed in or loaded automatically
  @property({ type: Object }) user: User | null = null;

  @state() private prefersDarkMql: MediaQueryList | null = null;

  // App-registered menu items, delivered by the SDK over 'moya-menu-update'.
  @state() private registeredMenuItems: MenuItemSnapshot[] = [];

  private initialized = false;
  private unsubscribeNavigationUpdate: (() => void) | null = null;
  private themeChangeHandler: ((e: MediaQueryListEvent) => void) | null = null;
  private menuUpdateHandler: ((e: Event) => void) | null = null;

  connectedCallback() {
    super.connectedCallback();
    // Explicitly read attributes that may not be converted correctly by Lit
    // This ensures attributes work when set via HTML (common in non-framework usage)
    if (this.hasAttribute('show-search')) {
      this.showSearch = true;
    }
    if (this.hasAttribute('show-messenger')) {
      this.showMessenger = true;
    }
    if (this.hasAttribute('show-breadcrumbs')) {
      this.showBreadcrumbs = true;
    }
    if (this.hasAttribute('show-app-context')) {
      this.showAppContext = true;
    }
    // Also read string attributes that are required for features to work
    const messengerUrl = this.getAttribute('messenger-url');
    if (messengerUrl) {
      this.messengerUrl = messengerUrl;
    }
    const messengerApiUrl = this.getAttribute('messenger-api-url');
    if (messengerApiUrl) {
      this.messengerApiUrl = messengerApiUrl;
    }
    const searchPlaceholder = this.getAttribute('search-placeholder');
    if (searchPlaceholder) {
      this.searchPlaceholder = searchPlaceholder;
    }
    const searchUrl = this.getAttribute('search-url');
    if (searchUrl) {
      this.searchUrl = searchUrl;
    }
    const pageTitle = this.getAttribute('page-title');
    if (pageTitle) {
      this.pageTitle = pageTitle;
    }
    // Allow apps to override theme via attribute (theme="light" | "dark" | "auto")
    const themeAttr = this.getAttribute('theme');
    if (themeAttr === 'light' || themeAttr === 'dark' || themeAttr === 'auto') {
      this.theme = themeAttr;
    }

    // Initialize theme (auto = prefers-color-scheme)
    this.initializeTheme();

    // Listen for navigation context updates from child apps
    this.unsubscribeNavigationUpdate = eventBus.on(
      Events.NAVIGATION_CONTEXT_UPDATE,
      this.handleNavigationContextUpdate.bind(this)
    );

    // Menu-contribution (P1): listen for the SDK's snapshot pushes, then ask
    // for the current set so a late-connecting header still gets populated.
    this.menuUpdateHandler = (e: Event) => this.handleMenuUpdate(e as CustomEvent);
    window.addEventListener('moya-menu-update', this.menuUpdateHandler);
    window.dispatchEvent(new CustomEvent('moya-menu-request'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.unsubscribeNavigationUpdate) {
      this.unsubscribeNavigationUpdate();
    }
    if (this.prefersDarkMql && this.themeChangeHandler) {
      this.prefersDarkMql.removeEventListener('change', this.themeChangeHandler);
    }
    if (this.menuUpdateHandler) {
      window.removeEventListener('moya-menu-update', this.menuUpdateHandler);
      this.menuUpdateHandler = null;
    }
  }

  private handleMenuUpdate(e: CustomEvent) {
    const detail = e.detail as { items?: MenuItemSnapshot[] } | null;
    this.registeredMenuItems = Array.isArray(detail?.items) ? detail!.items : [];
  }

  /** Registered items for a region, sorted by `order` asc (default 100). */
  private menuItemsForRegion(region: MenuRegion): MenuItemSnapshot[] {
    return this.registeredMenuItems
      .filter((item) => item.region === region)
      .slice()
      .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  }

  private handleMenuItemSelect(itemId: string) {
    this.dispatchEvent(
      new CustomEvent('moya-menu-select', {
        bubbles: true,
        composed: true,
        detail: { itemId },
      })
    );
  }

  /** nav region — top-bar nav entries near the app name / breadcrumb strip. */
  private renderNavMenuItems() {
    const items = this.menuItemsForRegion('nav');
    if (items.length === 0) return '';
    return html`
      <nav class="menu-nav" aria-label="Application menu">
        ${items.map((item) => this.renderNavMenuItem(item))}
      </nav>
    `;
  }

  private renderNavMenuItem(item: MenuItemSnapshot) {
    const showBadge = typeof item.badge === 'number' && item.badge > 0;
    const inner = html`
      ${item.icon
        ? html`<span class="menu-nav-icon" aria-hidden="true">${unsafeHTML(item.icon)}</span>`
        : ''}
      <span class="menu-nav-label">${item.label}</span>
      ${showBadge
        ? html`<span class="menu-badge" aria-label="${item.badge} new">${item.badge}</span>`
        : ''}
    `;
    if (item.href) {
      return html`
        <a
          class="menu-nav-item"
          href="${item.href}"
          aria-disabled="${item.disabled ? 'true' : 'false'}"
        >${inner}</a>
      `;
    }
    return html`
      <button
        class="menu-nav-item"
        type="button"
        ?disabled="${item.disabled ?? false}"
        @click="${() => this.handleMenuItemSelect(item.id)}"
      >${inner}</button>
    `;
  }

  /** actions region — icon buttons in the actions cluster next to the bell. */
  private renderActionMenuItems() {
    const items = this.menuItemsForRegion('actions');
    if (items.length === 0) return '';
    return html`${items.map((item) => this.renderActionMenuItem(item))}`;
  }

  private renderActionMenuItem(item: MenuItemSnapshot) {
    const showBadge = typeof item.badge === 'number' && item.badge > 0;
    // Icon-only controls: fold the label (and badge count) into the a11y name.
    const label = showBadge ? `${item.label} (${item.badge})` : item.label;
    const inner = html`
      <span class="menu-action-icon" aria-hidden="true">
        ${item.icon ? unsafeHTML(item.icon) : item.label.charAt(0).toUpperCase()}
      </span>
      ${showBadge ? html`<span class="menu-badge" aria-hidden="true">${item.badge}</span>` : ''}
    `;
    if (item.href) {
      return html`
        <a
          class="menu-action-item"
          href="${item.href}"
          aria-label="${label}"
          aria-disabled="${item.disabled ? 'true' : 'false'}"
        >${inner}</a>
      `;
    }
    return html`
      <button
        class="menu-action-item"
        type="button"
        aria-label="${label}"
        ?disabled="${item.disabled ?? false}"
        @click="${() => this.handleMenuItemSelect(item.id)}"
      >${inner}</button>
    `;
  }

  private initializeTheme() {
    if (this.theme === 'light' || this.theme === 'dark') {
      this.dataTheme = this.theme;
      return;
    }
    // auto: read prefers-color-scheme
    try {
      this.prefersDarkMql = window.matchMedia('(prefers-color-scheme: dark)');
      this.dataTheme = this.prefersDarkMql.matches ? 'dark' : 'light';
      this.themeChangeHandler = (e: MediaQueryListEvent) => {
        if (this.theme === 'auto') {
          this.dataTheme = e.matches ? 'dark' : 'light';
        }
      };
      this.prefersDarkMql.addEventListener('change', this.themeChangeHandler);
    } catch {
      // matchMedia unavailable (SSR / old browser) - keep default 'dark'
      this.dataTheme = 'dark';
    }
  }

  private handleNavigationContextUpdate(data: unknown) {
    const update = data as NavigationContextUpdate;
    if (update.appName !== undefined) {
      this.appName = update.appName;
    }
    if (update.pageTitle !== undefined) {
      this.pageTitle = update.pageTitle;
    }
    if (update.breadcrumbs !== undefined) {
      this.breadcrumbs = update.breadcrumbs;
    }

    // Re-emit as DOM event for parent components
    this.dispatchEvent(
      new CustomEvent('moya-navigation-context', {
        bubbles: true,
        composed: true,
        detail: update,
      })
    );
  }

  firstUpdated() {
    this.initialize();
  }

  private async initialize() {
    if (this.initialized) return;

    // Configure API service
    if (this.apiUrl) {
      this.initialized = true;
      configure(this.apiUrl, this.token);
    } else {
      // Wait a tick for React to set properties, then try again
      await new Promise(resolve => setTimeout(resolve, 0));
      // If host app didn't provide a token, try the domain-wide SSO cookie
      if (!this.token) {
        const match = document.cookie.match(/(?:^|;\s*)moya_token=([^;]*)/);
        if (match) {
          this.token = decodeURIComponent(match[1]);
          // Hydrate localStorage for the host app
          try {
            localStorage.setItem('moya_token', this.token);
            localStorage.setItem('token', this.token);
          } catch { /* storage unavailable */ }
        }
      }
      if (this.apiUrl) {
        this.initialized = true;
        configure(this.apiUrl, this.token);
      } else if (this.token) {
        // Even without apiUrl, set the token so user-menu JWT fallback works
        this.initialized = true;
        setToken(this.token);
      }
    }

    // Configure presence API separately (Platform Service for centralized status)
    if (this.presenceApiUrl) {
      configurePresence(this.presenceApiUrl);
    }

    // Configure the Notification Service separately, for the same reason:
    // notifications are their own host and are not served by `api-url`.
    if (this.notificationsUrl) {
      configureNotifications(this.notificationsUrl);
    }

    // Configure WebSocket. Prefer the notifications URL; `ws-url` stays
    // supported for apps that set only that.
    const notificationsWsUrl = this.notificationsUrl || this.wsUrl;
    if (notificationsWsUrl) {
      wsService.configure(notificationsWsUrl, this.token);
      wsService.connect();
    }

    // Load user if not provided
    if (!this.user && this.token && this.apiUrl) {
      try {
        this.user = await authAPI.getCurrentUser();
        eventBus.emit(Events.USER_LOADED, this.user);

        // Apply organization branding if user has active organization
        if (this.user?.activeOrganizationId && this.user?.organizations) {
          const activeOrg = this.user.organizations.find(
            (o) => o.id === this.user?.activeOrganizationId
          );
          if (activeOrg) {
            brandingService.applyOrganizationBranding(activeOrg);
          }
        }
      } catch (e) {
        console.error('[PlatformHeader] Failed to load user:', e);
      }
    }

    // Apply custom branding if provided
    if (this.primaryColor) {
      brandingService.applyBranding({ primaryColor: this.primaryColor });
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    // Update API configuration if apiUrl changes
    if (changedProperties.has('apiUrl') && this.apiUrl) {
      configure(this.apiUrl, this.token);
    }

    // Update presence API configuration if presenceApiUrl changes
    if (changedProperties.has('presenceApiUrl') && this.presenceApiUrl) {
      configurePresence(this.presenceApiUrl);
    }

    // Same for the Notification Service base URL.
    if (changedProperties.has('notificationsUrl') && this.notificationsUrl) {
      configureNotifications(this.notificationsUrl);
    }

    // Update token in services if changed
    if (changedProperties.has('token') && this.token) {
      setToken(this.token);
      wsService.setToken(this.token);
      // Also reconfigure API with token if apiUrl is set
      if (this.apiUrl) {
        configure(this.apiUrl, this.token);
      }
    }

    // Re-init theme listener if theme mode changed
    if (changedProperties.has('theme')) {
      if (this.theme === 'light' || this.theme === 'dark') {
        this.dataTheme = this.theme;
      } else if (this.theme === 'auto' && this.prefersDarkMql) {
        this.dataTheme = this.prefersDarkMql.matches ? 'dark' : 'light';
      }
    }
  }

  private handleAppSwitch(e: CustomEvent) {
    // Re-emit from platform header
    this.dispatchEvent(
      new CustomEvent('moya-app-switch', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private handleNotificationClick(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-notification-click', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private handleContextSwitch(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-context-switch', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private handleOrganizationSwitch(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-organization-switch', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private _getSignInUrl(): string {
    const callbackUrl = `${window.location.origin}/callback`;
    const url = new URL(this.portalUrl);
    url.searchParams.set('redirect_uri', callbackUrl);
    return url.toString();
  }

  private _handleSignIn() {
    // Save current URL so the app's callback can redirect back after login
    try {
      sessionStorage.setItem('moya_auth_return_url', window.location.href);
    } catch {
      // sessionStorage may be unavailable in some contexts
    }
    // Let the default <a href> navigation proceed
  }

  private handleLogout() {
    this.dispatchEvent(
      new CustomEvent('moya-logout', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleSearch(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-search', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private handleSearchSubmit(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-search-submit', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private handleSearchClear() {
    this.dispatchEvent(
      new CustomEvent('moya-search-clear', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleMessengerOpen(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-messenger-open', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  private handleMessengerClose() {
    this.dispatchEvent(
      new CustomEvent('moya-messenger-close', {
        bubbles: true,
        composed: true,
      })
    );
  }

  private handleBreadcrumbClick(e: CustomEvent) {
    this.dispatchEvent(
      new CustomEvent('moya-breadcrumb-click', {
        bubbles: true,
        composed: true,
        detail: e.detail,
      })
    );
  }

  render() {
    return html`
      ${this.showSkipLink
        ? html`<a class="skip-link" href="#${this.skipTargetId}">Skip to main content</a>`
        : ''}
      <header class="header" role="banner">
        <div class="header-left">
          ${this.showAppSwitcher
            ? html`
                <moya-app-switcher
                  .token="${this.token}"
                  .currentAppId="${this.appId}"
                  .portalUrl="${this.portalUrl}"
                  @moya-app-switch="${this.handleAppSwitch}"
                ></moya-app-switcher>
              `
            : ''}

          ${this.showLogo
            ? html`
                <a href="${this.portalUrl}" class="logo" aria-label="${this.logoText} home">
                  ${this.logoUrl
                    ? html`<img src="${this.logoUrl}" alt="${this.logoText}" style="height: 32px; width: auto;" />`
                    : html`
                        <div class="logo-icon" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="3" y="3" width="7" height="7" rx="1" />
                            <rect x="14" y="3" width="7" height="7" rx="1" />
                            <rect x="3" y="14" width="7" height="7" rx="1" />
                            <rect x="14" y="14" width="7" height="7" rx="1" />
                          </svg>
                        </div>
                      `}
                  <span class="logo-text">${this.logoText}</span>
                </a>
              `
            : ''}

          ${this.showAppContext && (this.appName || this.pageTitle || this.breadcrumbs.length > 0)
            ? html`
                <moya-app-context
                  .appName="${this.appName}"
                  .pageTitle="${this.pageTitle}"
                  .breadcrumbs="${this.breadcrumbs}"
                  ?showBreadcrumbs="${this.showBreadcrumbs}"
                  ?showAppName="${!!this.appName}"
                  @moya-breadcrumb-click="${this.handleBreadcrumbClick}"
                ></moya-app-context>
              `
            : this.appName
              ? html`<span class="app-name">${this.appName}</span>`
              : ''}

          ${this.renderNavMenuItems()}
        </div>

        <div class="header-center">
          ${this.showSearch && this.token
            ? html`
                <moya-global-search
                  placeholder="${this.searchPlaceholder}"
                  searchUrl="${this.searchUrl}"
                  search-api-url="${this.searchApiUrl}"
                  .token="${this.token}"
                  .debounceMs="${this.searchDebounceMs}"
                  @moya-search="${this.handleSearch}"
                  @moya-search-submit="${this.handleSearchSubmit}"
                  @moya-search-clear="${this.handleSearchClear}"
                ></moya-global-search>
              `
            : ''}
          ${this.showContextSwitcher && this.availableContexts.length > 0
            ? html`
                <moya-context-switcher
                  .currentContext="${this.currentContext}"
                  .availableContexts="${this.availableContexts}"
                  @moya-context-switch="${this.handleContextSwitch}"
                ></moya-context-switcher>
              `
            : ''}
        </div>

        <div class="header-right">
          ${this.showOrganizationSwitcher && this.user?.organizations && this.user.organizations.length > 1
            ? html`
                <moya-organization-switcher
                  .token="${this.token}"
                  .user="${this.user}"
                  @moya-organization-switch="${this.handleOrganizationSwitch}"
                ></moya-organization-switcher>
                <div class="divider" aria-hidden="true"></div>
              `
            : ''}

          ${this.showMessenger && this.messengerUrl && this.token
            ? html`
                <moya-messenger-icon
                  .token="${this.token}"
                  .messengerUrl="${this.messengerUrl}"
                  .messengerApiUrl="${this.messengerApiUrl}"
                  .userId="${this.user?.id || ''}"
                  @moya-messenger-open="${this.handleMessengerOpen}"
                  @moya-messenger-close="${this.handleMessengerClose}"
                ></moya-messenger-icon>
              `
            : ''}

          ${this.renderActionMenuItems()}

          ${this.showNotifications && this.token
            ? html`
                <moya-notification-bell
                  .token="${this.token}"
                  .notificationsUrl="${this.notificationsUrl || this.wsUrl}"
                  .notificationsAppUrl="${this.notificationsAppUrl}"
                  @moya-notification-click="${this.handleNotificationClick}"
                ></moya-notification-bell>
              `
            : ''}

          ${(this.showNotifications || this.showMessenger) && this.showUserMenu
            ? html`<div class="divider" aria-hidden="true"></div>`
            : ''}

          ${this.showUserMenu && this.token
            ? html`
                <moya-user-menu
                  .token="${this.token}"
                  .user="${this.user}"
                  ?avatarOnly="${this.avatarOnly}"
                  size="${this.userMenuSize}"
                  .customMenuItems="${this.customMenuItems}"
                  .registeredMenuItems="${this.menuItemsForRegion('user-menu')}"
                  @moya-organization-switch="${this.handleOrganizationSwitch}"
                  @moya-logout="${this.handleLogout}"
                ></moya-user-menu>
              `
            : !this.token
              ? html`
                  <a href="${this._getSignInUrl()}" class="sign-in-btn" @click="${this._handleSignIn}">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" stroke-linecap="round" stroke-linejoin="round" />
                      <polyline points="10 17 15 12 10 7" stroke-linecap="round" stroke-linejoin="round" />
                      <line x1="15" y1="12" x2="3" y2="12" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    Sign In
                  </a>
                `
              : ''}
        </div>
      </header>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-platform-header': MoyaPlatformHeader;
  }
}
